import { getContactTemplateFieldValue } from "./contactTemplateValues";
import { Info } from "lucide-react";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { ApplyInsuranceDropdown } from "@/components/features/matter/file-repo/GenerateDocument/ApplyInsuranceDropdown";
import { ApplyVehicleDropdown } from "@/components/features/matter/file-repo/GenerateDocument/ApplyVehicleDropdown";
import {
  BrowserDocxTemplatePreview,
  type PreviewVariableConfig,
} from "@/components/features/matter/file-repo/GenerateDocument/BrowserDocxTemplatePreview";
import {
  CreatableSelect,
  type SelectOption,
} from "@/components/features/matter/file-repo/GenerateDocument/CreatableSelect";
import {
  buildInsuranceWithConnections,
  buildIncidentReportSourceConnections,
  buildMedicalTreatmentProviderConnections,
  getMedicalTreatmentProviderContactIds,
  type ContactConnectionWithContact,
  type InsuranceWithConnections,
} from "@/components/features/matter/file-repo/GenerateDocument/templateDictionaryOptions";
import { buildTemplateUsers, getTemplateUserValues } from "./templateUsers";
import {
  buildSettlementNegotiationContactConnections,
  getSettlementNegotiationContactIds,
  isSettlementNegotiationContactConnection,
} from "./settlementNegotiationTemplateContacts";
import {
  buildContextualTemplateDictionaryOptions,
  getTemplateDefaultOptions,
} from "./buildContextualTemplateDictionaryOptions";
import {
  AppModal,
  Button,
  Form,
  FormItem,
  FormSubmitButton,
  Input,
  MultiselectFormField,
  SearchableSelectFormField,
  Separator,
  Textarea,
  TextFormField,
  Typography,
} from "@/components/ui";
import { ActionConfirmationModal } from "@/components/ui/ActionConfirmationModal/ActionConfirmationModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import { useUser } from "@/components/UserProvider";
import { useVehiclesViewModel } from "@/hooks/matter-view-models/use-vehicles-view-model";
import { useFormatInsurancePolicyLimits } from "@/hooks/useFormatInsurancePolicyLimits";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  getFirmOfficesQueryOptions,
  getUsersOptions,
  templateContactsQueryOptions,
} from "@/lib/data/react-query";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useGenerateMatterTemplateDocument } from "@/lib/data/react-query/matters/matter-template";
import type { MATTER_TEMPLATE_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import type { TemplateDictionaryItem } from "@/lib/data/static/templateDictionaryItems";
import { templateDictionaryItems } from "@/lib/data/static/templateDictionaryItems";
import type { Matter, MatterDocumentSubItem, MatterFile } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getInsuranceContactConnections } from "@/lib/utils/connections/insurance-contact-connections";
import {
  formatAddress,
  formatAddressLine1,
  formatAddressLine2,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { getFileNameParts } from "@/lib/utils/matterFile";
import {
  getMatterContactConnections,
  getThirdPartyMatterContactIds,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { decodeTemplateUserRoleAlias } from "@/lib/utils/templateUserRoleVariable";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { groupBy, snakeCase, sortBy, startCase, uniq } from "lodash";
import pluralize from "pluralize";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface GenerateDocumentSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterFileIdLocation?: string;
  matter: Matter;
  afterGenerateFn?: (results: MatterFile[]) => void;
  templateType?: (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number];
  restrictTemplatesToType?: boolean;
  hideSaveAndMerge?: boolean;
  subItem?: MatterDocumentSubItem;
}

const formSchema = z.object({
  id: z.string().min(1, {
    message: "Please select a template",
  }),
  generatedFileName: z.string().min(1, {
    message: "Please enter a file name",
  }),
  fileType: z
    .array(z.enum([".pdf", ".docx"]))
    .refine((value) => value.length > 0, {
      message: "Must select atleast one file type",
    }),
});

const defaultObject = {
  id: "",
  generatedFileName: "",
  fileType: [".pdf"] as (".pdf" | ".docx")[],
};

const GENERATE_DOCUMENT_FORM_ID = "generate-document-form";

type GenerateDocumentFormData = z.infer<typeof formSchema>;

type PendingSubmissionState = {
  formData: GenerateDocumentFormData;
  formattedInputs: Record<string, string>;
  missingCount: number;
};

type ParsedVariable = {
  baseVariable: string;
  alias?: string;
  format?: string;
  instance?: number;
};

type TemplateDictionaryFormItem = TemplateDictionaryItem & {
  baseVariable: string;
  baseKey: string;
  isFormattingVariant: boolean;
  displayTitle: string;
  alias?: string;
  instance?: number;
};

export type InstanceGroup = {
  key: string;
  alias?: string;
  instance: number;
  items: TemplateDictionaryFormItem[];
};

const FORMAT_TOKENS = new Set([
  "upperCase",
  "lowerCase",
  "titleCase",
  "plural",
  "possessive",
  "short",
  "long",
]);

const CONTACT_ROLE_PREFIXES = [
  ["client", "Client"],
  ["defendant", "Defendant"],
  ["witness", "Witness"],
  ["provider", "Provider"],
  ["primaryContact", "Primary Contact"],
  ["incidentReportSource", "Incident Report Source"],
  ["bodilyInjuryAdjuster", "Bodily Injury Adjuster"],
  ["propertyDamageAdjuster", "Property Damage Adjuster"],
  ["pdAdjuster", "Property Damage Adjuster"],
  ["medPayAdjuster", "MedPay Adjuster"],
  ["adjusterSupervisor", "Adjuster Supervisor"],
  ["adjuster", "Adjuster"],
] as const;

const normalizeBaseVariable = (baseVar: string) => {
  const match = CONTACT_ROLE_PREFIXES.find(([prefix]) =>
    baseVar.startsWith(prefix),
  );
  if (!match) {
    return { normalized: baseVar, role: undefined as string | undefined };
  }
  const [prefix, role] = match;
  return {
    normalized: baseVar.replace(new RegExp(`^${prefix}`), "contact"),
    role,
  };
};

const getRoleLabelForKey = (key: string) => {
  const match = CONTACT_ROLE_PREFIXES.find(([prefix]) => prefix === key);
  return match?.[1];
};

const parseVariable = (variable: string): ParsedVariable => {
  const [base, ...rest] = variable.split("_");
  let format: string | undefined;
  let instance: number | undefined;
  const aliasParts: string[] = [];

  rest.forEach((part) => {
    if (!Number.isNaN(Number(part))) {
      if (instance === undefined) {
        instance = Number(part);
      }
      return;
    }
    if (!format && FORMAT_TOKENS.has(part)) {
      format = part;
      return;
    }
    aliasParts.push(part);
  });

  return {
    baseVariable: base,
    alias: aliasParts.length > 0 ? aliasParts.join("_") : undefined,
    format,
    instance,
  };
};

export const getBaseKeyForVariable = (variable: string): string => {
  const parsed = parseVariable(variable);
  const aliasPart = parsed.alias ? `_${parsed.alias}` : "";
  const instancePart = parsed.instance ? `_${parsed.instance}` : "";
  return `${parsed.baseVariable}${aliasPart}${instancePart}`;
};

const getSectionGroupStateKey = (sectionKey: string, group: InstanceGroup) =>
  `${sectionKey}::${group.alias ?? group.instance}`;

const buildInstanceGroups = (
  items: TemplateDictionaryFormItem[],
): InstanceGroup[] => {
  const groups: InstanceGroup[] = [];
  items.forEach((item) => {
    const { alias, instance } =
      item.parent === "custom"
        ? { alias: undefined, instance: 1 }
        : parseVariable(item.variable);
    const resolvedInstance = instance ?? 1;
    const key =
      item.parent === "custom"
        ? "custom"
        : alias
          ? `alias:${alias}`
          : `instance:${resolvedInstance}`;
    const existingGroup = groups.find((group) => group.key === key);
    if (existingGroup) {
      existingGroup.items.push(item);
    } else {
      groups.push({
        key,
        alias,
        instance: resolvedInstance,
        items: [item],
      });
    }
  });
  return groups;
};

const getGroupHeadingSuffix = (
  group: InstanceGroup,
  totalGroups: number,
): string => {
  if (group.alias) {
    return ` (${startCase(group.alias)})`;
  }
  if (totalGroups > 1 || group.instance > 1) {
    return ` #${group.instance}`;
  }
  return "";
};

const getVisibleGroupItems = (items: TemplateDictionaryFormItem[]) => {
  const representativeByBaseKey = new Map<string, TemplateDictionaryFormItem>();

  items.forEach((item) => {
    const existing = representativeByBaseKey.get(item.baseKey);
    if (!existing) {
      representativeByBaseKey.set(item.baseKey, item);
      return;
    }

    if (existing.isFormattingVariant && !item.isFormattingVariant) {
      representativeByBaseKey.set(item.baseKey, item);
    }
  });

  return items.filter(
    (item) => representativeByBaseKey.get(item.baseKey) === item,
  );
};

export type InsuranceVariablePrefix = "clientInsurance" | "defendantInsurance";
export type VehicleVariablePrefix = "clientVehicle" | "defendantVehicle";

const applyFormatToken = (format: string, value: string) => {
  switch (format) {
    case "upperCase":
      return value.toUpperCase();
    case "lowerCase":
      return value.toLowerCase();
    case "titleCase":
      return startCase(value);
    case "plural":
      return pluralize(value);
    case "possessive":
      const words = value.split(" ");
      const lastWord = words[words.length - 1];
      words[words.length - 1] = lastWord.endsWith("s")
        ? lastWord + "'"
        : lastWord + "'s";
      return words.join(" ");
    case "short":
      return dayjs.utc(value).format("M/D/YYYY").toString();
    case "long":
      return dayjs.utc(value).format("MMMM D, YYYY").toString();
    default:
      return value;
  }
};

const formatVariableValue = (value: string, variable: string) => {
  if (!value) return value;

  const { format } = parseVariable(variable);
  if (!format) return value;

  return applyFormatToken(format, value);
};

const formatMergePayloadValue = (value: string, variable: string) => {
  if (!value) return value;

  const { baseVariable } = parseVariable(variable);
  const templateItem = templateDictionaryItems.find(
    (item) => item.variable === baseVariable,
  );

  if (!templateItem?.lockedDisplayAsMultiline) {
    return value;
  }

  return value.replace(/\r?\n/g, "<br>");
};

export const useAutoSelectSingleOption = (
  options: SelectOption[],
  selectedValue: string,
  onSelect: (value: string) => void,
  enabled = true,
) => {
  useEffect(() => {
    if (!enabled || selectedValue || options.length !== 1) return;
    const defaultValue = options[0]?.value;
    if (!defaultValue) return;
    onSelect(defaultValue);
  }, [enabled, onSelect, options, selectedValue]);
};

interface SectionApplySelectProps {
  placeholder: string;
  options: SelectOption[];
  selectionKey: string;
  selectionValue: string;
  createMessage: string;
  setSectionContactSelections: Dispatch<SetStateAction<Record<string, string>>>;
  onApply: (value: string) => void;
  autoSelectSingleOption?: boolean;
}

const SectionApplySelect = ({
  placeholder,
  options,
  selectionKey,
  selectionValue,
  createMessage,
  setSectionContactSelections,
  onApply,
  autoSelectSingleOption = true,
}: SectionApplySelectProps) => {
  const handleChange = useCallback(
    (val: string) => {
      setSectionContactSelections((prev) => ({
        ...prev,
        [selectionKey]: val,
      }));
      onApply(val);
    },
    [onApply, selectionKey, setSectionContactSelections],
  );

  useAutoSelectSingleOption(
    options,
    selectionValue,
    handleChange,
    autoSelectSingleOption,
  );

  return (
    <CreatableSelect
      className="!space-y-0"
      placeholder={placeholder}
      options={options}
      value={selectionValue}
      onChange={handleChange}
      createMessage={createMessage}
    />
  );
};

export const GenerateDocumentSlideover = ({
  open,
  setOpen,
  matterFileIdLocation,
  matter,
  templateType,
  restrictTemplatesToType = false,
  afterGenerateFn,
  hideSaveAndMerge = false,
  subItem,
}: GenerateDocumentSlideoverProps) => {
  const { currentUser, hasPagePermission } = useUser();
  const queryClient = useQueryClient();
  const { openMergeDocumentModal } = useGlobalModal();
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [multiCategorySelectOpen, setMultiCategorySelectOpen] = useState(false);
  const [nextAction, setNextAction] = useState<"save" | "save-and-merge">(
    hideSaveAndMerge ? "save" : "save",
  );
  const form = useForm<GenerateDocumentFormData>({
    resolver: zodResolver(formSchema),
    values: defaultObject,
    mode: "onSubmit",
  });
  const selectedFileTypes = form.watch("fileType", defaultObject.fileType);
  const hasPdfSelected = Array.isArray(selectedFileTypes)
    ? selectedFileTypes.includes(".pdf")
    : false;

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const selectedTemplate = matterSettingsData?.data.templates.find(
    (template) => template.id === form.watch("id"),
  );
  const templateFileNameParts = getFileNameParts(selectedTemplate?.name);
  const templateFileName = templateFileNameParts.fileExtension
    ? selectedTemplate?.name
    : selectedTemplate?.presignedUrlKey;
  const templateFile = useMemo(
    () => ({
      fileName: templateFileName ?? "",
      presignedURLKey: selectedTemplate?.presignedUrlKey || "",
    }),
    [templateFileName, selectedTemplate?.presignedUrlKey],
  );
  const { data: firmOffices } = useQuery(getFirmOfficesQueryOptions());
  const { data: firmUsers } = useQuery(getUsersOptions(open));
  const templateUsers = useMemo(
    () => buildTemplateUsers(matter, firmUsers?.data),
    [matter, firmUsers?.data],
  );

  const formatInsurancePolicyLimits = useFormatInsurancePolicyLimits();

  const generateMatterTemplateDocument = useGenerateMatterTemplateDocument();

  // Track a quick-select contact for a whole section (e.g., "contact")
  const [sectionContactSelections, setSectionContactSelections] = useState<
    Record<string, string>
  >({});
  // Track format overrides for specific variables (e.g., userInitials)
  const [formatOverrides, setFormatOverrides] = useState<
    Record<string, string>
  >({});
  const [incompleteFieldsModalOpen, setIncompleteFieldsModalOpen] =
    useState(false);
  const [pendingSubmission, setPendingSubmission] =
    useState<PendingSubmissionState | null>(null);

  const graphContactIds = useMemo(
    () =>
      uniq(
        [
          ...getMatterContactConnections(matter).map((connection) => ({
            targetObjectId: connection.contactId,
          })),
          ...matter.vehicles.flatMap((vehicle) => vehicle.connections),
          ...matter.insurances.flatMap((insurance) => insurance.connections),
          ...matter.incidentReports.flatMap((report) => report.connections),
          ...getMedicalTreatmentProviderContactIds(matter).map(
            (targetObjectId) => ({ targetObjectId }),
          ),
          ...getSettlementNegotiationContactIds(matter).map(
            (targetObjectId) => ({
              targetObjectId,
            }),
          ),
        ].map((connection) => connection.targetObjectId),
      ),
    [matter],
  );
  const {
    data: graphContactsData,
    isLoading: contactsLoading,
    isError: contactsError,
    refetch: refetchContacts,
  } = useQuery(
    templateContactsQueryOptions(
      graphContactIds,
      queryClient,
      hasPagePermission("contacts.detail"),
    ),
  );
  const graphContacts = useMemo(
    () => graphContactsData ?? [],
    [graphContactsData],
  );
  const contactsWithConnections = useMemo<ContactConnectionWithContact[]>(
    () => [
      ...getMatterContactConnections(matter)
        .filter(
          (connection) =>
            !connection.incidentReportId &&
            !isSettlementNegotiationContactConnection(connection) &&
            connection.label !== "Provider",
        )
        .map((connection) => ({
          ...connection,
          contact:
            graphContacts.find(
              (contact) => contact.id === connection.contactId,
            ) ?? connection.contact,
        })),
      ...buildIncidentReportSourceConnections(
        matter.incidentReports,
        graphContacts,
      ),
      ...buildMedicalTreatmentProviderConnections(matter, graphContacts),
      ...buildSettlementNegotiationContactConnections(matter, graphContacts),
    ],
    [graphContacts, matter],
  );

  const client = contactsWithConnections.find(
    (connection) => connection.label === "Client",
  );

  const thirdPartyContactIds = getThirdPartyMatterContactIds(matter);

  const insurancesWithConnections: InsuranceWithConnections[] =
    matter.insurances.map((insurance) =>
      buildInsuranceWithConnections(
        insurance,
        graphContacts,
        formatInsurancePolicyLimits(insurance),
      ),
    );
  const clientInsurance = insurancesWithConnections.filter((insurance) =>
    [
      ...getInsuranceContactConnections(insurance, "Insured"),
      ...getInsuranceContactConnections(insurance, "Policy Holder"),
    ].some((connection) => connection.targetObjectId === client?.contactId),
  );

  const defendantInsurance = insurancesWithConnections.filter((insurance) =>
    [
      ...getInsuranceContactConnections(insurance, "Insured"),
      ...getInsuranceContactConnections(insurance, "Policy Holder"),
    ].some((connection) => thirdPartyContactIds.has(connection.targetObjectId)),
  );

  const massTortCustomFieldValues = useMemo(
    () =>
      new Map(
        matter.massTortCustomFields.flatMap((customField) => {
          const definition = customField.customField;
          const label = definition?.label;
          return label
            ? [
                [
                  `custom_${snakeCase(label)}`,
                  {
                    definition,
                    value: customField.value ?? "",
                  },
                ],
              ]
            : [];
        }),
      ),
    [matter.massTortCustomFields],
  );

  const unknownItems: TemplateDictionaryFormItem[] = sortBy(
    selectedTemplate?.variables
      ?.filter((item) => {
        const baseVariable = item.split("_")[0];
        // Exclude signer fields (contact or user) from unknown items
        if (
          baseVariable.startsWith("Signer") ||
          baseVariable.startsWith("UserSigner")
        ) {
          return false;
        }
        return !templateDictionaryItems.some(
          (dictionaryItem) => dictionaryItem.variable === baseVariable,
        );
      })
      .map((item) => {
        const { baseVariable, instance, alias } = parseVariable(item);
        const baseKey = getBaseKeyForVariable(item);
        const customField = massTortCustomFieldValues.get(baseKey);
        const suffix = alias
          ? ` (${startCase(decodeTemplateUserRoleAlias(alias))})`
          : instance
            ? ` #${instance}`
            : "";
        const displayTitle =
          customField?.definition.label ??
          `${startCase(baseVariable)}${suffix}`;
        return {
          title: displayTitle,
          variable: item,
          parent: customField ? "custom" : ("unknown" as const),
          type: "string" as const,
          options: customField?.definition.valueOptions ?? undefined,
          baseVariable,
          baseKey,
          isFormattingVariant: item !== baseKey,
          displayTitle,
          alias,
          instance,
        };
      }),
    ["title"],
  ) as TemplateDictionaryFormItem[];

  const groupedTemplateDictionaryItemsByParent = groupBy(
    sortBy(
      templateDictionaryItems.filter((item) => {
        // Exclude signer fields from document generation
        if (item.parent === "signer" || item.parent === "userSigner") {
          return false;
        }

        return selectedTemplate?.variables?.some(
          (templateVar) => templateVar.split("_")[0] === item.variable,
        );
      }),
      ["title"],
    )
      .flatMap((item) => {
        const matchingVariables =
          selectedTemplate?.variables?.filter(
            (v) => v.split("_")[0] === item.variable,
          ) ?? [];

        if (matchingVariables.length === 0) {
          const baseKey = getBaseKeyForVariable(item.variable);
          return [
            {
              ...item,
              baseVariable: item.variable,
              baseKey,
              isFormattingVariant: item.variable !== baseKey,
              displayTitle: item.title,
              alias: undefined,
              instance: undefined,
            },
          ];
        }

        return matchingVariables.map((variableName) => {
          const parsed = parseVariable(variableName);
          const baseKey = getBaseKeyForVariable(variableName);
          const suffix = parsed.alias
            ? ` (${startCase(decodeTemplateUserRoleAlias(parsed.alias))})`
            : parsed.instance
              ? ` #${parsed.instance}`
              : "";

          return {
            ...item,
            title: `${item.title}${suffix}`,
            variable: variableName,
            baseVariable: item.variable,
            baseKey,
            isFormattingVariant: variableName !== baseKey,
            displayTitle: `${item.title}${suffix}`,
            alias: parsed.alias,
            instance: parsed.instance,
          };
        });
      })
      // @ts-expect-error - TODO: fix this
      .concat(unknownItems),
    "parent",
  ) as Partial<Record<string, TemplateDictionaryFormItem[]>>;
  const templateDictionaryItemsGroupedByParent = {
    ...groupedTemplateDictionaryItemsByParent,
    firmUser: groupedTemplateDictionaryItemsByParent.firmUser ?? [],
  } as Record<string, TemplateDictionaryFormItem[]>;

  const {
    clientVehicles: classifiedClientVehicles,
    defendantVehicles: classifiedDefendantVehicles,
  } = useVehiclesViewModel(matter.id);
  const {
    templateDictionaryOptions,
    defaultTemplateDictionaryOptions,
    clientVehicles,
    defendantVehicles,
  } = buildContextualTemplateDictionaryOptions(
    {
      matter,
      firm: currentUser.firm,
      firmOffices: firmOffices?.data,
      templateUsers,
      contacts: contactsWithConnections,
      clientInsurance,
      defendantInsurance,
      clientVehicles: classifiedClientVehicles,
      defendantVehicles: classifiedDefendantVehicles,
      vehicleOwnerContacts: graphContacts,
    },
    subItem,
  );

  // Build options for the contact section quick selector
  const contactSectionOptions = useMemo(
    () =>
      contactsWithConnections.map((c) => ({
        // Use connection id to disambiguate multiple roles for same contact
        value: c.id,
        label: formatContactsName(c.contact) ?? "",
        context: c.label,
      })),
    [contactsWithConnections],
  );

  // Filtered sets for insurance/adjuster roles
  const clientSectionOptions = useMemo(
    () => contactSectionOptions.filter((o) => o.context === "Client"),
    [contactSectionOptions],
  );
  const bodilyInjuryAdjusterSectionOptions = useMemo(
    () =>
      contactSectionOptions.filter(
        (o) => o.context === "Bodily Injury Adjuster",
      ),
    [contactSectionOptions],
  );
  const propertyDamageAdjusterSectionOptions = useMemo(
    () =>
      contactSectionOptions.filter(
        (o) => o.context === "Property Damage Adjuster",
      ),
    [contactSectionOptions],
  );
  const medPayAdjusterSectionOptions = useMemo(
    () => contactSectionOptions.filter((o) => o.context === "MedPay Adjuster"),
    [contactSectionOptions],
  );
  const adjusterSupervisorSectionOptions = useMemo(
    () =>
      contactSectionOptions.filter((o) => o.context === "Adjuster Supervisor"),
    [contactSectionOptions],
  );
  const defendantSectionOptions = useMemo(
    () => contactSectionOptions.filter((o) => o.context === "Defendant"),
    [contactSectionOptions],
  );
  const witnessSectionOptions = useMemo(
    () => contactSectionOptions.filter((o) => o.context === "Witness"),
    [contactSectionOptions],
  );
  const primaryContactSectionOptions = useMemo(
    () => contactSectionOptions.filter((o) => o.context === "Primary Contact"),
    [contactSectionOptions],
  );
  const incidentReportSourceSectionOptions = useMemo(
    () =>
      contactSectionOptions.filter(
        (o) => o.context === "Incident Report Source",
      ),
    [contactSectionOptions],
  );
  const medicalProviderSectionOptions = useMemo(
    () => contactSectionOptions.filter((o) => o.context === "Provider"),
    [contactSectionOptions],
  );

  // Role matches lead aliased groups; all matter users still precede other firm users.
  const getUserSectionOptionsForGroup = useCallback(
    (group: InstanceGroup) => {
      const roleLabel = group.alias
        ? decodeTemplateUserRoleAlias(group.alias)
        : undefined;
      const orderedUsers = roleLabel
        ? [
            ...templateUsers.filter(({ roles }) => roles.includes(roleLabel)),
            ...templateUsers.filter(({ roles }) => !roles.includes(roleLabel)),
          ]
        : templateUsers;
      return orderedUsers.map(({ isMatterUser, user, roles }) => ({
        group: isMatterUser ? "Assigned to matter" : "Other firm users",
        value: user.id,
        label: getTemplateUserValues(user).userName,
        context: roles.length ? roles.join(", ") : "Firm user",
      }));
    },
    [templateUsers],
  );

  // Build options for the firm office section quick selector
  const firmOfficeSectionOptions = useMemo(() => {
    return firmOffices?.data
      .filter((office) => office.label)
      .map((office) => ({
        value: office.id,
        label: office.label,
      }));
  }, [firmOffices?.data]);

  // Apply a selected contact only to a specific instance in the section
  const applySectionContactInstance = (
    connectionId: string,
    group: InstanceGroup,
  ) => {
    const connection = contactsWithConnections.find(
      (c) => c.id === connectionId,
    );
    if (!connection) return;

    const updates: Record<string, string> = {};
    const items = group.items;

    items.forEach((item) => {
      const base = item.baseVariable || item.variable;
      const normalizedBase = normalizeBaseVariable(base).normalized;
      const targetKey = getBaseKeyForVariable(item.variable);
      const value = getContactTemplateFieldValue(connection, normalizedBase);
      if (value !== undefined) {
        updates[targetKey] = value;
      }
    });

    if (Object.keys(updates).length) {
      setSelectedTemplateDictionaryOptions((prev) => ({ ...prev, ...updates }));
    }
  };

  // Apply a selected firm office only to a specific instance in the section

  const applySectionFirmOfficeGroup = (
    firmOfficeId: string,
    group: InstanceGroup,
  ) => {
    const firmOffice = firmOffices?.data.find((o) => o.id === firmOfficeId);
    if (!firmOffice) return;
    const updates: Record<string, string> = {};
    const items = group.items;
    items.forEach((item) => {
      const base = item.baseVariable || item.variable;
      const targetKey = getBaseKeyForVariable(item.variable);
      switch (base) {
        case "firmOfficeAddressLine1":
          updates[targetKey] = formatAddressLine1({
            street1: firmOffice.street1,
            street2: firmOffice.street2 ?? undefined,
          });
          break;
        case "firmOfficeAddressLine2":
          updates[targetKey] = formatAddressLine2({
            city: firmOffice.city,
            state: firmOffice.state,
            zipCode: firmOffice.zipCode,
          });
          break;
        case "firmOfficeAddress":
          updates[targetKey] = formatAddress({
            street1: firmOffice.street1,
            street2: firmOffice.street2 ?? undefined,
            city: firmOffice.city,
            state: firmOffice.state,
            zipCode: firmOffice.zipCode,
            country: firmOffice.country,
          });
          break;
        case "firmOfficePhoneNumber":
          updates[targetKey] = formatLocalOrIntlPhoneNumber({
            number: firmOffice.phoneNumber,
          });
          break;
        default:
          break;
      }
    });
    if (Object.keys(updates).length) {
      setSelectedTemplateDictionaryOptions((prev) => ({ ...prev, ...updates }));
    }
  };

  // Apply a selected user only to a specific instance in the section
  const applySectionUserGroup = (id: string, group: InstanceGroup) => {
    const selectedUser = templateUsers.find(({ user }) => user.id === id);
    if (!selectedUser) return;
    const values = getTemplateUserValues(selectedUser.user);
    const updates: Record<string, string> = {};
    group.items.forEach((item) => {
      const base = item.baseVariable || item.variable;
      if (base in values) {
        updates[getBaseKeyForVariable(item.variable)] = values[base];
      }
    });
    if (Object.keys(updates).length) {
      setSelectedTemplateDictionaryOptions((prev) => ({ ...prev, ...updates }));
    }
  };

  const [
    selectedTemplateDictionaryOptions,
    setSelectedTemplateDictionaryOptions,
  ] = useState<{ [key: string]: string }>({});

  const matterTemplates =
    templateType && !showAllTemplates
      ? (matterSettingsData?.data.templates.filter((template) =>
          template.category?.includes(templateType),
        ) ?? [])
      : (matterSettingsData?.data.templates ?? []);

  const matterTemplatesWithCategories: {
    category: string;
    items: { label: string; value: string; disabled?: boolean }[];
  }[] = [
    {
      category: `${templateType} templates`,
      items: (
        matterSettingsData?.data.templates.filter((template) =>
          template.category?.includes(
            templateType as (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number],
          ),
        ) ?? []
      ).map((category) => ({
        label: getFileNameParts(category.name).fileName,
        value: category.id,
      })),
    },
    {
      category: "Other" as const,
      items: (
        matterSettingsData?.data.templates.filter(
          (template) =>
            !template.category?.includes(
              templateType as (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number],
            ),
        ) ?? []
      ).map((category) => ({
        label: getFileNameParts(category.name).fileName,
        value: category.id,
      })),
    },
  ];

  if (matterTemplatesWithCategories[0].items.length === 0) {
    matterTemplatesWithCategories[0].items = [
      {
        label: "No results",
        value: "<No options available>",
        disabled: true,
      },
    ];
  }

  const buildTemplateInputs = useCallback(() => {
    const templateVariables =
      selectedTemplate?.variables?.filter(
        (variable) =>
          !variable.startsWith("Signer") && !variable.startsWith("UserSigner"),
      ) ?? [];

    const missingVariables: string[] = [];

    const formattedInputs = templateVariables.reduce<Record<string, string>>(
      (acc, variable) => {
        const parsed = parseVariable(variable);
        const baseVariable = parsed.baseVariable;
        const baseKey = getBaseKeyForVariable(variable);

        let value =
          selectedTemplateDictionaryOptions[variable] ||
          selectedTemplateDictionaryOptions[baseKey] ||
          selectedTemplateDictionaryOptions[baseVariable] ||
          massTortCustomFieldValues.get(baseKey)?.value ||
          "";

        if (value === "") {
          missingVariables.push(variable);
          return acc;
        }

        if (baseVariable === "userInitials") {
          const override = formatOverrides[variable];
          if (override === "upperCase") value = value.toUpperCase();
          if (override === "lowerCase") value = value.toLowerCase();
        }

        acc[variable] = formatMergePayloadValue(
          formatVariableValue(value, variable),
          variable,
        );
        return acc;
      },
      {},
    );

    return { formattedInputs, missingVariables };
  }, [
    formatOverrides,
    massTortCustomFieldValues,
    selectedTemplate?.variables,
    selectedTemplateDictionaryOptions,
  ]);
  const buildBrowserPreviewInputs = useCallback(
    () => buildTemplateInputs().formattedInputs,
    [buildTemplateInputs],
  );
  const getPreviewVariableConfig = (
    variable: string,
  ): PreviewVariableConfig => {
    let sectionKey: string | undefined;
    let item: TemplateDictionaryFormItem | undefined;
    for (const [key, items] of Object.entries(
      templateDictionaryItemsGroupedByParent,
    )) {
      const match = items.find((candidate) => candidate.variable === variable);
      if (match) {
        sectionKey = key;
        item = match;
        break;
      }
    }

    if (!item) {
      return { label: startCase(variable), options: [] };
    }
    // Contact options describe template roles; generation uses profile values.
    if (item.options && item.parent !== "contact") {
      return {
        label: item.displayTitle,
        options: item.options.map((option) => ({
          label: option,
          value: option,
        })),
      };
    }

    let options = templateDictionaryOptions[item.baseVariable];
    let usedContactPool = false;
    if (!options) {
      const { normalized } = normalizeBaseVariable(item.baseVariable);
      let normalizedKey = normalized;
      if (normalizedKey === item.baseVariable) {
        normalizedKey = item.baseVariable.replace(/^insurance/, "contact");
      }
      if (normalizedKey !== item.baseVariable) {
        options = templateDictionaryOptions[normalizedKey] ?? [];
        usedContactPool = normalizedKey.startsWith("contact");
      } else {
        options = [];
      }
    }

    if (usedContactPool) {
      const role =
        sectionKey === "insurance"
          ? "Insurer"
          : getRoleLabelForKey(sectionKey ?? "");
      if (role) {
        options = options.filter(
          (option) =>
            typeof option.context === "string" &&
            (option.context === role || option.context.includes(` - ${role}`)),
        );
      }
    }

    return { label: item.displayTitle, options };
  };
  const handlePreviewVariableSelect = useCallback(
    (variable: string, value: string) => {
      setSelectedTemplateDictionaryOptions((previous) => ({
        ...previous,
        [getBaseKeyForVariable(variable)]: value,
      }));
    },
    [],
  );

  const executeGenerateDocument = useCallback(
    async (
      data: GenerateDocumentFormData,
      formattedInputs: Record<string, string>,
    ) => {
      try {
        const results = await Promise.all(
          data.fileType.map((fileType) =>
            generateMatterTemplateDocument.mutateAsync({
              id: data.id,
              matterId: matter.id,
              matterFileName: `${(data.generatedFileName || selectedTemplate?.name) ?? "generatedTemplate"}${fileType}`,
              matterTemplateInputs: formattedInputs,
              matterFileIdLocation: matterFileIdLocation ?? "",
              matterFileExtension: fileType,
            }),
          ),
        );
        const generatedFiles = results.map((r) => r.data);
        setOpen(false);
        form.reset();
        setSelectedTemplateDictionaryOptions({});
        setSectionContactSelections({});
        setFormatOverrides({});
        afterGenerateFn?.(generatedFiles);
        const pdfGeneratedFileIds = results.reduce<string[]>(
          (acc, result, index) => {
            if (data.fileType[index] === ".pdf") {
              const fileId = result.data.id;
              if (fileId) {
                acc.push(fileId);
              }
            }
            return acc;
          },
          [],
        );
        if (nextAction === "save-and-merge" && pdfGeneratedFileIds.length > 0) {
          openMergeDocumentModal({
            matterId: matter.id,
            initialSelectedFileIds: pdfGeneratedFileIds,
          });
        }
      } finally {
        setNextAction("save");
      }
    },
    [
      afterGenerateFn,
      form,
      generateMatterTemplateDocument,
      matter.id,
      matterFileIdLocation,
      nextAction,
      openMergeDocumentModal,
      selectedTemplate?.name,
      setFormatOverrides,
      setOpen,
      setSectionContactSelections,
      setSelectedTemplateDictionaryOptions,
    ],
  );

  const submitGenerateDocument = async (
    data: GenerateDocumentFormData,
    options?: {
      bypassMissingCheck?: boolean;
      formattedInputsOverride?: Record<string, string>;
    },
  ) => {
    if (contactsLoading || contactsError) return;
    let formattedInputs: Record<string, string>;
    let missingVariables: string[] = [];

    if (options?.formattedInputsOverride !== undefined) {
      formattedInputs = options.formattedInputsOverride;
    } else {
      const { formattedInputs: builtInputs, missingVariables: missing } =
        buildTemplateInputs();
      formattedInputs = builtInputs;
      missingVariables = missing;
    }

    if (!options?.bypassMissingCheck && missingVariables.length > 0) {
      setPendingSubmission({
        formData: data,
        formattedInputs,
        missingCount: missingVariables.length,
      });
      setIncompleteFieldsModalOpen(true);
      return;
    }

    await executeGenerateDocument(data, formattedInputs);
  };

  const handleGenerateDocument = async (data: GenerateDocumentFormData) => {
    await submitGenerateDocument(data);
  };

  const handleContinueWithMissingFields = async () => {
    if (!pendingSubmission) return;
    const submission = pendingSubmission;
    setPendingSubmission(null);
    setIncompleteFieldsModalOpen(false);
    await submitGenerateDocument(submission.formData, {
      bypassMissingCheck: true,
      formattedInputsOverride: submission.formattedInputs,
    });
  };

  const handleMissingFieldsGoBack = () => {
    setPendingSubmission(null);
    setIncompleteFieldsModalOpen(false);
  };

  useEffect(() => {
    if (contactsLoading || contactsError || !selectedTemplate?.variables)
      return;

    // Get all variables including their format/instance
    const variables = selectedTemplate.variables;

    // Collect any auto-selectable defaults in a single update to avoid thrash
    const updates: Record<string, string> = {};
    variables.forEach((variable) => {
      const parsed = parseVariable(variable);
      const baseVar = parsed.baseVariable;
      // Firm-wide choices are manual; user defaults come from Apply User or a matter role.
      if (baseVar.startsWith("user")) return;
      const storageKey = getBaseKeyForVariable(variable);
      const { normalized: normalizedBaseVar, role } =
        baseVar in templateDictionaryOptions
          ? { normalized: baseVar, role: undefined }
          : normalizeBaseVariable(baseVar);

      const preferredOptions = (
        getTemplateDefaultOptions(
          normalizedBaseVar,
          templateDictionaryOptions,
          defaultTemplateDictionaryOptions,
        )[normalizedBaseVar] ?? []
      ).filter(
        (option) =>
          !role ||
          option.context === role ||
          option.context.includes(` - ${role}`),
      );
      const candidates = preferredOptions;
      const defaultValue = candidates[0]?.value;
      if (
        candidates.length === 1 &&
        defaultValue.trim() &&
        !(storageKey in selectedTemplateDictionaryOptions)
      ) {
        updates[storageKey] = defaultValue;
      }
    });

    if (Object.keys(updates).length > 0) {
      setSelectedTemplateDictionaryOptions((prev) => {
        const missingDefaults = Object.fromEntries(
          Object.entries(updates).filter(([key]) => !(key in prev)),
        );
        return Object.keys(missingDefaults).length > 0
          ? { ...prev, ...missingDefaults }
          : prev;
      });
    }
  }, [
    contactsLoading,
    contactsError,
    templateDictionaryOptions,
    defaultTemplateDictionaryOptions,
    selectedTemplate?.variables,
    selectedTemplateDictionaryOptions,
  ]);

  useEffect(() => {
    const firmUserItems = templateDictionaryItemsGroupedByParent.firmUser;
    if (!firmUserItems.length) {
      return;
    }

    const updates: Record<string, string> = {};
    const nextSelections: Record<string, string> = {};

    buildInstanceGroups(firmUserItems).forEach((group) => {
      if (!group.alias) {
        return;
      }

      const roleLabel = decodeTemplateUserRoleAlias(group.alias);
      const matchingAssignees = matter.assignees.filter(
        (item) => !item.dateTimeEnd && item.role.label === roleLabel,
      );

      if (matchingAssignees.length !== 1) {
        return;
      }
      const assignee = matchingAssignees[0];

      nextSelections[getSectionGroupStateKey("firmUser", group)] =
        assignee.user.id;

      const values = getTemplateUserValues(assignee.user);
      group.items.forEach((item) => {
        if (item.baseVariable in values) {
          updates[getBaseKeyForVariable(item.variable)] =
            values[item.baseVariable];
        }
      });
    });

    if (Object.keys(updates).length > 0) {
      setSelectedTemplateDictionaryOptions((prev) => {
        const missingDefaults = Object.fromEntries(
          Object.entries(updates).filter(([key]) => !(key in prev)),
        );

        return Object.keys(missingDefaults).length > 0
          ? { ...prev, ...missingDefaults }
          : prev;
      });
    }

    if (Object.keys(nextSelections).length > 0) {
      setSectionContactSelections((prev) => {
        const missingSelections = Object.fromEntries(
          Object.entries(nextSelections).filter(([key]) => !(key in prev)),
        );

        return Object.keys(missingSelections).length > 0
          ? { ...prev, ...missingSelections }
          : prev;
      });
    }
  }, [matter.assignees, templateDictionaryItemsGroupedByParent.firmUser]);

  useEffect(() => {
    if (!open) {
      setShowAllTemplates(false);
      form.reset();
      setSelectedTemplateDictionaryOptions({});
      setSectionContactSelections({});
      setFormatOverrides({});
      setNextAction("save");
    }
  }, [open, form]);

  useEffect(() => {
    if (selectedTemplate) {
      form.setValue(
        "generatedFileName",
        getFileNameParts(selectedTemplate.name).fileName,
      );
    }
  }, [selectedTemplate, form]);

  // Clear quick "Apply" selections and field values when switching templates
  useEffect(() => {
    setSectionContactSelections({});
    setSelectedTemplateDictionaryOptions({});
    setFormatOverrides({});
  }, [selectedTemplate?.id]);

  const missingFieldsCount = pendingSubmission?.missingCount ?? 0;
  const missingFieldsDescription =
    missingFieldsCount > 0
      ? `${missingFieldsCount} template ${pluralize("field", missingFieldsCount)} ${missingFieldsCount === 1 ? "is" : "are"} still empty. Go back to fill them in or continue anyway.`
      : "Some template fields are still empty. Go back to fill them in or continue anyway.";

  const previewNode =
    selectedTemplate && !contactsLoading && !contactsError ? (
      <BrowserDocxTemplatePreview
        open={open}
        templateId={selectedTemplate.id}
        templateFile={templateFile}
        buildInputs={buildBrowserPreviewInputs}
        getVariableConfig={getPreviewVariableConfig}
        onVariableSelect={handlePreviewVariableSelect}
      />
    ) : undefined;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          size={selectedTemplate ? "3xl" : "2xl"}
          overlayClassName="z-[106] bg-black/50 backdrop-blur-md"
          className={cn(
            "z-[107] transition-[width,height,max-width,max-height,border-radius] duration-300 ease-out will-change-[width,height,max-width,max-height,border-radius]",
            selectedTemplate &&
              "h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none rounded-none",
          )}
          bodyContained
          title={"Generate document"}
          description={`Generate a new document for this matter`}
          loading={generateMatterTemplateDocument.isPending}
          isDirty={form.formState.isDirty}
          preview={previewNode}
          previewLabel={null}
          previewPosition="right"
          previewColumnClassName="lg:w-[60%] lg:max-w-none"
          footer={
            <div className="flex w-full justify-end gap-2">
              <FormCancelButton
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              {!hideSaveAndMerge && (
                <FormSubmitButton
                  variant="light-primary"
                  title="Save then merge"
                  submitFormId={GENERATE_DOCUMENT_FORM_ID}
                  loading={
                    generateMatterTemplateDocument.isPending &&
                    nextAction === "save-and-merge"
                  }
                  overrideDisabled
                  disabled={
                    !hasPdfSelected ||
                    contactsLoading ||
                    contactsError ||
                    generateMatterTemplateDocument.isPending
                      ? true
                      : undefined
                  }
                  submitFn={() => {
                    setNextAction("save-and-merge");
                  }}
                />
              )}
              <FormSubmitButton
                submitFormId={GENERATE_DOCUMENT_FORM_ID}
                loading={
                  generateMatterTemplateDocument.isPending &&
                  nextAction === "save"
                }
                overrideDisabled
                disabled={
                  contactsLoading ||
                  contactsError ||
                  generateMatterTemplateDocument.isPending
                    ? true
                    : undefined
                }
                submitFn={() => {
                  setNextAction("save");
                }}
              />
            </div>
          }
        >
          <div className="flex grow flex-col overflow-hidden">
            <form
              id={GENERATE_DOCUMENT_FORM_ID}
              className="flex grow flex-col overflow-hidden"
              onSubmit={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await form.handleSubmit(handleGenerateDocument)();
              }}
            >
              <div className="overflow-y-auto">
                <fieldset
                  className={cn(
                    slideoverFieldsetClassName,
                    "grid-cols-1 md:grid-cols-2",
                  )}
                >
                  <div className="space-y-2">
                    {templateType && !restrictTemplatesToType ? (
                      <MultiCategorySelectFormField
                        label="Select template"
                        buttonClassName="min-w-[200px]"
                        register={form.register("id")}
                        items={matterTemplatesWithCategories}
                        placeholder="Select template..."
                        emptyMessage="No templates found."
                        open={multiCategorySelectOpen}
                        setOpen={setMultiCategorySelectOpen}
                        isItemsError={isMatterSettingsError}
                        isItemsLoading={isMatterSettingsLoading}
                        onChange={() => {
                          setMultiCategorySelectOpen(false);
                        }}
                      />
                    ) : (
                      <SearchableSelectFormField
                        items={matterTemplates}
                        dropdownFooter={
                          templateType && restrictTemplatesToType ? (
                            <button
                              type="button"
                              className="hover:bg-light-primary focus-visible:bg-light-primary text-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm font-normal outline-hidden select-none"
                              onClick={() => {
                                setShowAllTemplates((current) => !current);
                              }}
                            >
                              {showAllTemplates
                                ? `Show ${templateType.toLowerCase()} templates`
                                : "Show all templates"}
                            </button>
                          ) : null
                        }
                        register={form.register("id")}
                        label="Select template"
                        dropdownHeading={
                          templateType && !showAllTemplates
                            ? `${templateType} templates`
                            : "All templates"
                        }
                        itemsKey="id"
                        buttonClassName="min-w-[200px]"
                        formatItemLabel={(item) =>
                          getFileNameParts(item?.name).fileName
                        }
                        formatItemContext={(item) => item?.description}
                        placeholder="Select template..."
                        emptyMessage={
                          templateType &&
                          !showAllTemplates &&
                          matterTemplates.length === 0 ? (
                            <div className="flex items-center justify-center gap-1.5 px-3">
                              <span>No templates found.</span>
                              <TooltipWrapper message="Set template categories in Firm → Templates → Edit → Categories.">
                                <button
                                  type="button"
                                  aria-label="How to add a template category"
                                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm focus-visible:ring-2 focus-visible:outline-none"
                                >
                                  <Info className="h-4 w-4" />
                                </button>
                              </TooltipWrapper>
                            </div>
                          ) : (
                            "No templates found."
                          )
                        }
                        isItemsLoading={isMatterSettingsLoading}
                        isItemsError={isMatterSettingsError}
                      />
                    )}
                  </div>
                  <TextFormField
                    register={form.register("generatedFileName")}
                    label="Generated file name"
                    placeholder="Enter a file name"
                  />
                  <MultiselectFormField
                    register={form.register("fileType")}
                    label="Generated file types"
                    options={[
                      {
                        value: ".pdf",
                        label: "PDF",
                      },
                      {
                        value: ".docx",
                        label: "DOCX",
                      },
                    ]}
                  />
                  {/* <div></div> */}
                  <div className="col-span-full">
                    <Separator />
                  </div>

                  {contactsLoading && (
                    <div role="status">
                      <Typography>Loading contact details…</Typography>
                    </div>
                  )}
                  {contactsError && (
                    <div role="alert" className="col-span-full space-y-2">
                      <Typography>
                        Unable to load contact details. Try again before
                        generating the document.
                      </Typography>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => void refetchContacts()}
                      >
                        Try again
                      </Button>
                    </div>
                  )}
                  {selectedTemplate &&
                    !hasPagePermission("contacts.detail") && (
                      <Typography className="col-span-full">
                        Some contact fields may require manual entry with your
                        current access.
                      </Typography>
                    )}
                  {Object.keys(templateDictionaryItemsGroupedByParent).map(
                    (key, i) => {
                      if (contactsLoading || contactsError) return null;
                      const itemsForKey =
                        templateDictionaryItemsGroupedByParent[key] ?? [];
                      const instanceGroups = buildInstanceGroups(itemsForKey);

                      return (
                        <Fragment key={key}>
                          {instanceGroups.map((group, idx) => {
                            const groupSuffix = getGroupHeadingSuffix(
                              group,
                              instanceGroups.length,
                            );
                            const sectionLabel =
                              getRoleLabelForKey(key) ?? startCase(key);
                            const headingLabel = `${sectionLabel}${groupSuffix}`;
                            const selectionKey = getSectionGroupStateKey(
                              key,
                              group,
                            );
                            const selectionValue =
                              sectionContactSelections[selectionKey] || "";
                            const applySuffix = group.alias
                              ? ` (${startCase(group.alias)})`
                              : instanceGroups.length > 1 || group.instance > 1
                                ? ` #${group.instance}`
                                : "";

                            return (
                              <Fragment key={`${key}-${group.key}`}>
                                <div className="col-span-full flex flex-wrap items-center gap-2">
                                  <Typography variant="heading">
                                    {headingLabel}
                                  </Typography>
                                  {key === "contact" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Contact${applySuffix}`}
                                        options={contactSectionOptions}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "client" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Client${applySuffix}`}
                                        options={clientSectionOptions}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {(key === "clientVehicle" ||
                                    key === "defendantVehicle") && (
                                    <ApplyVehicleDropdown
                                      templateDictionaryOptions={
                                        templateDictionaryOptions
                                      }
                                      autoSelectSingleOption={
                                        subItem?.type !== "vehicle" ||
                                        subItem.vehicleParty ===
                                          (key === "clientVehicle"
                                            ? "client"
                                            : "defendant")
                                      }
                                      defaultItemId={
                                        subItem?.type === "vehicle" &&
                                        subItem.vehicleParty ===
                                          (key === "clientVehicle"
                                            ? "client"
                                            : "defendant")
                                          ? subItem.id
                                          : undefined
                                      }
                                      sectionKey={key as VehicleVariablePrefix}
                                      applySuffix={applySuffix}
                                      selectionKey={selectionKey}
                                      selectionValue={selectionValue}
                                      group={group}
                                      setSectionContactSelections={
                                        setSectionContactSelections
                                      }
                                      setSelectedTemplateDictionaryOptions={
                                        setSelectedTemplateDictionaryOptions
                                      }
                                      clientVehicles={clientVehicles}
                                      defendantVehicles={defendantVehicles}
                                    />
                                  )}
                                  {(key === "clientInsurance" ||
                                    key === "defendantInsurance") && (
                                    <ApplyInsuranceDropdown
                                      defaultItemId={
                                        subItem?.type === "insurance"
                                          ? subItem.id
                                          : undefined
                                      }
                                      sectionKey={
                                        key as InsuranceVariablePrefix
                                      }
                                      applySuffix={applySuffix}
                                      selectionKey={selectionKey}
                                      selectionValue={selectionValue}
                                      group={group}
                                      setSectionContactSelections={
                                        setSectionContactSelections
                                      }
                                      setSelectedTemplateDictionaryOptions={
                                        setSelectedTemplateDictionaryOptions
                                      }
                                      clientInsurance={clientInsurance}
                                      defendantInsurance={defendantInsurance}
                                    />
                                  )}
                                  {key === "defendant" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Defendant${applySuffix}`}
                                        options={defendantSectionOptions}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "witness" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Witness${applySuffix}`}
                                        options={witnessSectionOptions}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "primaryContact" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Primary Contact${applySuffix}`}
                                        options={primaryContactSectionOptions}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "incidentReportSource" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Incident Report Source${applySuffix}`}
                                        options={
                                          incidentReportSourceSectionOptions
                                        }
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "provider" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Provider${applySuffix}`}
                                        options={medicalProviderSectionOptions}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {(key === "bodilyInjuryAdjuster" ||
                                    key === "adjuster") && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Bodily Injury Adjuster${applySuffix}`}
                                        options={
                                          bodilyInjuryAdjusterSectionOptions
                                        }
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "propertyDamageAdjuster" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Property Damage Adjuster${applySuffix}`}
                                        options={
                                          propertyDamageAdjusterSectionOptions
                                        }
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "medPayAdjuster" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply MedPay Adjuster${applySuffix}`}
                                        options={medPayAdjusterSectionOptions}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "adjusterSupervisor" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Adjuster Supervisor${applySuffix}`}
                                        options={
                                          adjusterSupervisorSectionOptions
                                        }
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create contact"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionContactInstance(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "firmOffice" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply Firm Office${applySuffix}`}
                                        options={firmOfficeSectionOptions ?? []}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create user"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionFirmOfficeGroup(
                                            val,
                                            group,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {key === "firmUser" && (
                                    <div className="w-[260px]">
                                      <SectionApplySelect
                                        placeholder={`Apply User${applySuffix}`}
                                        autoSelectSingleOption={
                                          !group.alias &&
                                          matter.assignees.some(
                                            (assignee) => !assignee.dateTimeEnd,
                                          )
                                        }
                                        options={getUserSectionOptionsForGroup(
                                          group,
                                        )}
                                        selectionKey={selectionKey}
                                        selectionValue={selectionValue}
                                        createMessage="Create user"
                                        setSectionContactSelections={
                                          setSectionContactSelections
                                        }
                                        onApply={(val) => {
                                          applySectionUserGroup(val, group);
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>

                                {getVisibleGroupItems(group.items).map(
                                  (dictionaryItem) => {
                                    const baseVariable =
                                      dictionaryItem.baseVariable;
                                    const storageKey = getBaseKeyForVariable(
                                      dictionaryItem.variable,
                                    );
                                    const fieldValue =
                                      selectedTemplateDictionaryOptions[
                                        storageKey
                                      ] ||
                                      massTortCustomFieldValues.get(storageKey)
                                        ?.value ||
                                      "";
                                    const resolveOptions = () => {
                                      let opts =
                                        templateDictionaryOptions[baseVariable];
                                      let usedContactPool = false;
                                      if (!opts) {
                                        const { normalized } =
                                          normalizeBaseVariable(baseVariable);
                                        let normalizedKey = normalized;
                                        if (normalizedKey === baseVariable) {
                                          normalizedKey = baseVariable.replace(
                                            /^insurance/,
                                            "contact",
                                          );
                                        }
                                        if (normalizedKey !== baseVariable) {
                                          opts =
                                            templateDictionaryOptions[
                                              normalizedKey
                                            ] ?? [];
                                          usedContactPool =
                                            normalizedKey.startsWith("contact");
                                        } else {
                                          opts = [];
                                        }
                                      }
                                      if (usedContactPool) {
                                        const role =
                                          key === "insurance"
                                            ? "Insurer"
                                            : getRoleLabelForKey(key);
                                        if (role) {
                                          return opts.filter(
                                            (o) =>
                                              typeof o.context === "string" &&
                                              (o.context === role ||
                                                o.context.includes(
                                                  ` - ${role}`,
                                                )),
                                          );
                                        }
                                      }
                                      return opts;
                                    };

                                    const options =
                                      dictionaryItem.options &&
                                      dictionaryItem.parent !== "contact"
                                        ? dictionaryItem.options.map(
                                            (option) => ({
                                              value: option,
                                              label: option,
                                            }),
                                          )
                                        : resolveOptions();
                                    const isEditableGeneratedTotalField =
                                      dictionaryItem.variable.startsWith(
                                        "total",
                                      ) ||
                                      dictionaryItem.variable ===
                                        "netClientIssued";
                                    const handleChange = (value: string) => {
                                      setSelectedTemplateDictionaryOptions(
                                        (prev) => ({
                                          ...prev,
                                          [storageKey]: value,
                                        }),
                                      );
                                    };
                                    const commonProps = {
                                      placeholder: `Select or enter a ${dictionaryItem.displayTitle}`,
                                      value: fieldValue,
                                      onChange: handleChange,
                                      createMessage: `Add ${dictionaryItem.displayTitle}`,
                                      label: dictionaryItem.displayTitle,
                                      disabled:
                                        dictionaryItem.lockedAtGeneration,
                                    };

                                    if (dictionaryItem.lockedAtGeneration) {
                                      if (
                                        dictionaryItem.lockedDisplayAsMultiline
                                      ) {
                                        return (
                                          <FormItem
                                            key={dictionaryItem.variable}
                                            className="col-span-full"
                                          >
                                            <div className="space-y-2">
                                              <div className="text-sm font-medium">
                                                {dictionaryItem.displayTitle}
                                              </div>
                                              <Textarea
                                                value={fieldValue}
                                                onChange={(event) => {
                                                  handleChange(
                                                    event.target.value,
                                                  );
                                                }}
                                                rows={Math.max(
                                                  3,
                                                  fieldValue.split(
                                                    /<br\s*\/?>|\r?\n/i,
                                                  ).length,
                                                )}
                                                placeholder={`Auto-generated ${dictionaryItem.displayTitle}`}
                                                className="min-h-[120px] w-full resize-y whitespace-pre-wrap"
                                              />
                                            </div>
                                          </FormItem>
                                        );
                                      }

                                      return (
                                        <FormItem key={dictionaryItem.variable}>
                                          <div className="space-y-2">
                                            <div className="text-sm font-medium">
                                              {dictionaryItem.displayTitle}
                                            </div>
                                            <Input
                                              value={fieldValue}
                                              onChange={(event) => {
                                                handleChange(
                                                  event.target.value,
                                                );
                                              }}
                                              readOnly={
                                                !isEditableGeneratedTotalField
                                              }
                                              disabled={
                                                !isEditableGeneratedTotalField
                                              }
                                              placeholder={`Auto-generated ${dictionaryItem.displayTitle}`}
                                            />
                                          </div>
                                        </FormItem>
                                      );
                                    }

                                    if (
                                      templateDictionaryOptions[
                                        baseVariable
                                      ]?.[0]?.value
                                    ) {
                                      return (
                                        <FormItem key={dictionaryItem.variable}>
                                          <div>
                                            <CreatableSelect
                                              {...commonProps}
                                              emptyMessage={`No ${dictionaryItem.displayTitle} found`}
                                              options={options}
                                            />
                                          </div>
                                        </FormItem>
                                      );
                                    }

                                    return (
                                      <FormItem key={dictionaryItem.variable}>
                                        <div>
                                          <CreatableSelect
                                            {...commonProps}
                                            emptyMessage={`No ${dictionaryItem.displayTitle} found. Start typing to create one.`}
                                            options={options}
                                          />
                                        </div>
                                      </FormItem>
                                    );
                                  },
                                )}

                                {idx < instanceGroups.length - 1 && (
                                  <div className="col-span-full">
                                    <Separator />
                                  </div>
                                )}
                              </Fragment>
                            );
                          })}

                          {i <
                            Object.keys(templateDictionaryItemsGroupedByParent)
                              .length -
                              1 && (
                            <div className="col-span-full">
                              <Separator />
                            </div>
                          )}
                        </Fragment>
                      );
                    },
                  )}
                </fieldset>
              </div>
            </form>
          </div>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        open={incompleteFieldsModalOpen}
        setOpen={setIncompleteFieldsModalOpen}
        title="Some fields are empty"
        description={missingFieldsDescription}
        buttonText="Continue"
        cancelText="Go back"
        actionVariant="default"
        afterActionFn={handleContinueWithMissingFields}
        onCancel={handleMissingFieldsGoBack}
      />
    </>
  );
};
